import * as React from "react";
import { useEffect, useRef } from "react";

function isEditableTarget(t: EventTarget | null): boolean {
  const el = t as HTMLElement | null;
  if (!el) return false;
  if ((el as any).isContentEditable) return true;
  const tag = el.tagName?.toLowerCase();
  return tag === "input" || tag === "textarea" || tag === "select";
}

export function BarcodeInputHandler(props: {
  enabled: boolean;
  onBarcode: (barcode: string) => void;
  minLength?: number;
  maxGapMs?: number;
}) {
  const { enabled, onBarcode, minLength = 3, maxGapMs = 60 } = props;

  const bufferRef = useRef("");
  const lastAtRef = useRef<number | null>(null);
  const flushTimerRef = useRef<number | null>(null);

  useEffect(() => {
    if (!enabled) return;

    const flush = () => {
      if (flushTimerRef.current !== null) {
        window.clearTimeout(flushTimerRef.current);
        flushTimerRef.current = null;
      }
      const code = bufferRef.current.trim();
      bufferRef.current = "";
      lastAtRef.current = null;
      if (code.length >= minLength) {
        onBarcode(code);
      }
    };

    const scheduleFlush = () => {
      if (flushTimerRef.current !== null) window.clearTimeout(flushTimerRef.current);
      flushTimerRef.current = window.setTimeout(() => flush(), maxGapMs + 30);
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (!enabled) return;
      if (e.defaultPrevented) return;
      if (isEditableTarget(e.target)) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      if (e.key === "Enter") {
        if (bufferRef.current.length > 0) {
          e.preventDefault();
          flush();
        }
        return;
      }

      if (e.key.length !== 1) return;

      const now = Date.now();
      const lastAt = lastAtRef.current;
      if (lastAt !== null && now - lastAt > maxGapMs) {
        // Too slow -> treat as a new scan.
        bufferRef.current = "";
      }
      lastAtRef.current = now;
      bufferRef.current += e.key;
      scheduleFlush();
    };

    window.addEventListener("keydown", onKeyDown, { capture: true });
    return () => {
      window.removeEventListener("keydown", onKeyDown, { capture: true } as any);
      if (flushTimerRef.current !== null) window.clearTimeout(flushTimerRef.current);
    };
  }, [enabled, maxGapMs, minLength, onBarcode]);

  return null;
}

